import {
  DropdownButton,
  DropdownButtonContext,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ConversationLanguageCode } from 'lib-shared-languages';
import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';

import { isDetailedError } from 'apis/ai/types/aiApi.types';
import {
  TranslateTextLanguageSelectionList,
  TranslateTextLanguageSelectionListProps,
} from 'components/translateTextLanguageSelectionList';
import {
  languageCodesToSelectionListValue,
  LanguageSelectionListValue,
} from 'utils/useLanguageSelectionList';
import { useTranslateText } from 'utils/useTranslateText';

/** The status of translation. */
export type TranslationStatus = AsyncStatus | 'notNeeded';

export type TranslateTextChangeData = {
  /** The selected language. */
  readonly selectedLanguage?: LanguageSelectionListValue;
  /** The original untranslated text. */
  readonly originalText: string;
  /** The translated text; */
  readonly translatedText?: string;
  /** The feedback id from the endpoint that produced the translation. */
  readonly feedbackId?: string;
  /** The status of the translation. */
  readonly status: TranslationStatus;
  /** The error detail code */
  readonly errorDetailCode?: string;
};

type TranslateTextDropdownSelectionListProps = {
  /** The message to translate. */
  message: string;
  /** The selected language code to which the text should be translated to. */
  selectedLanguageCode?: ConversationLanguageCode;
  /** The original language of the text. */
  originalLanguageCode: ConversationLanguageCode | null;
  /** The callback for change of translation. */
  onChange?: (event: TranslateTextChangeData) => void;
  /** The csid */
  csid?: string;
} & Pick<TranslateTextLanguageSelectionListProps, 'onRevert'>;

/**
 * Represents a dropdown component for translating given text to a selected
 * language.
 */
export const TranslateTextDropdownSelectionList = (
  props: TranslateTextDropdownSelectionListProps,
): JSX.Element => {
  const { message, originalLanguageCode, onChange, onRevert, csid } = props;
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(
    props.selectedLanguageCode,
  );

  useEffect(
    () => setSelectedLanguageCode(props.selectedLanguageCode),
    [props.selectedLanguageCode],
  );

  const selectedLanguage = useMemo(() => {
    return selectedLanguageCode
      ? languageCodesToSelectionListValue([selectedLanguageCode])[0]
      : undefined;
  }, [selectedLanguageCode]);
  const [translate, translateResponse] = useTranslateText();
  const isTranslating = translateResponse.asyncStatus === 'pending';

  useEffect(() => {
    if (translateResponse.error) {
      return;
    }
    if (
      translateResponse.asyncStatus !== 'fulfilled' ||
      // When receiving a response, only change when selected language of the response is the same as the selected language
      // Otherwise we discard the response as a new one should have been requested
      // This fixes a race condition
      (translateResponse.asyncStatus === 'fulfilled' &&
        selectedLanguage &&
        selectedLanguage.name === translateResponse.originalArg?.language)
    ) {
      onChange?.({
        originalText: message,
        selectedLanguage,
        translatedText: translateResponse.translatedText,
        feedbackId: translateResponse.feedbackId,
        status: translateResponse.asyncStatus,
      });
    }
    // we only care about the changes in the response
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateResponse]);

  // Handles translating and reverting.
  useEffect(() => {
    if (selectedLanguage) {
      if (selectedLanguageCode === originalLanguageCode) {
        onChange?.({
          originalText: message,
          selectedLanguage,
          status: 'notNeeded',
        });
      } else {
        translate({
          language: selectedLanguage.name,
          text: message,
          csid,
        }).catch((error) => {
          if (isDetailedError(error)) {
            onChange?.({
              originalText: message,
              selectedLanguage,
              status: 'rejected',
              errorDetailCode: error.data.detail,
            });
          }

          console.warn('Could not translate: ', error);
        });
      }
    } else {
      onChange?.({
        originalText: message,
        status: 'uninitialized',
      });
    }
    // Only run when selected language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const dropdown = useMemo(
    () => (
      <DropdownButtonContext.Consumer>
        {(dropdownProps) => (
          <TranslateTextLanguageSelectionList
            autoFocusSearch={dropdownProps.dropdownOpen}
            selectedLanguageCode={selectedLanguageCode}
            onRevert={onRevert}
            onLanguageChange={(event) => {
              if (event.selectedLanguage) {
                ampli.agentCopilotTranslateMessageLanguageSelected({
                  language: event.selectedLanguage.name,
                  context: 'singleMessage',
                });
              }

              setSelectedLanguageCode(event.selectedLanguage?.code);
              dropdownProps.closeDropdown();
            }}
          />
        )}
      </DropdownButtonContext.Consumer>
    ),
    [selectedLanguageCode, onRevert],
  );

  return (
    <Tooltip content="Translate">
      <DropdownButton
        fixed
        noArrow
        icon="translate"
        buttonProps={{
          isLoading: isTranslating,
          disabled: isTranslating,
        }}
        dropdown={dropdown}
      />
    </Tooltip>
  );
};
